import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Form,
  Button,
  Container,
  Row,
  Col,
  Alert,
  Spinner,
  Card,
} from "react-bootstrap";
import axiosClient from "../../axios";

const AffiliateDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [affiliate, setAffiliate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [edited, setEdited] = useState(false);
  const [saving, setSaving] = useState(false);

  const statusOptions = [
    "pending",
    "active",
    "inactive",
    "suspended",
    "rejected",
  ];

  const fetchAffiliate = async () => {
    try {
      const response = await axiosClient.get(`/api/affiliates/${id}`);
      setAffiliate({
        ...response.data.affiliate,
        share_allocation: response.data.share_allocation,
      });
    } catch (err) {
      console.error(err);
      setError("Failed to fetch affiliate details.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAffiliate();
  }, [id]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setAffiliate((prev) => ({
      ...prev,
      [name]: ["child_commission", "share_amount"].includes(name)
        ? Number(value)
        : value,
    }));

    setEdited(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setError("");
    setSuccess("");
    setSaving(true);

    try {
      await axiosClient.patch(`/api/affiliates/${id}`, {
        bank_name: affiliate.bank_name,
        banking_name: affiliate.banking_name,
        account_number: affiliate.account_number,
        ifsc_code: affiliate.ifsc_code,
        address: affiliate.address,
        status: affiliate.status,
        child_commission: affiliate.child_commission,
        share_amount: affiliate.share_amount,
      });

      // Refresh all values by fetching latest data from API
      await fetchAffiliate();

      setSuccess("Changes saved successfully!");
      setEdited(false);
      setSaving(false);

      // Auto-hide success message after 3 seconds
      setTimeout(() => {
        setSuccess("");
      }, 3000);
    } catch (err) {
      console.error(err);
      setSaving(false);

      if (err.response?.status === 422) {
        const errors = err.response.data.errors;
        setError(Object.values(errors).flat().join(" "));
      } else {
        setError(
          err.response?.data?.message ||
            "Something went wrong while updating affiliate.",
        );
      }
    }
  };

  const handleBack = () => {
    navigate("/xl-admin/affiliates");
  };

  if (loading) {
    return (
      <Container className="text-center mt-5">
        <Spinner animation="border" variant="primary" />
        <p>Loading affiliate details...</p>
      </Container>
    );
  }

  if (!affiliate) {
    return (
      <Container className="text-center mt-5">
        <Alert variant="danger">Affiliate not found</Alert>
        <Button onClick={handleBack}>Back to Affiliates</Button>
      </Container>
    );
  }

  return (
    <Container>
      <Button variant="secondary" onClick={handleBack} className="mb-4">
        Back to Affiliates
      </Button>

      <h3 className="mb-4">Affiliate Details</h3>

      {error && <Alert variant="danger">{error}</Alert>}
      {success && <Alert variant="success">{success}</Alert>}

      {/* USER DETAILS */}
      <Card className="mb-4">
        <Card.Body>
          <h5 className="mb-3">User Information</h5>

          <Row className="mb-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label>Name</Form.Label>
                <Form.Control
                  type="text"
                  value={affiliate.user?.name || ""}
                  readOnly
                />
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group>
                <Form.Label>Email</Form.Label>
                <Form.Control
                  type="email"
                  value={affiliate.user?.email || ""}
                  readOnly
                />
              </Form.Group>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* AFFILIATE DETAILS */}
      <Card className="mb-4">
        <Card.Body>
          <h5 className="mb-3">Affiliate Information</h5>

          <Row className="mb-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label>Affiliate ID</Form.Label>
                <Form.Control type="text" value={affiliate.id || ""} readOnly />
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group>
                <Form.Label>Referral Code</Form.Label>
                <Form.Control
                  type="text"
                  value={affiliate.referral_code || ""}
                  readOnly
                />
              </Form.Group>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label>Status</Form.Label>
                <Form.Select
                  name="status"
                  value={affiliate.status || ""}
                  onChange={handleChange}
                >
                  {statusOptions.map((status) => (
                    <option key={status} value={status}>
                      {status}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group>
                <Form.Label>Current Share Allocation</Form.Label>
                <Form.Control
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  name="share_amount"
                  value={
                    affiliate.share_amount ??
                    affiliate.share_allocation?.share_amount ??
                    0
                  }
                  onChange={handleChange}
                />
              </Form.Group>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* BANK DETAILS */}
      <Card className="mb-4">
        <Card.Body>
          <h5 className="mb-3">Bank Details</h5>

          <Row className="mb-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label>Bank Name</Form.Label>
                <Form.Control
                  type="text"
                  name="bank_name"
                  value={affiliate.bank_name || ""}
                  onChange={handleChange}
                />
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group>
                <Form.Label>Account Holder Name</Form.Label>
                <Form.Control
                  type="text"
                  name="banking_name"
                  value={affiliate.banking_name || ""}
                  onChange={handleChange}
                />
              </Form.Group>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label>Account Number</Form.Label>
                <Form.Control
                  type="text"
                  name="account_number"
                  value={affiliate.account_number || ""}
                  onChange={handleChange}
                />
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group>
                <Form.Label>IFSC Code</Form.Label>
                <Form.Control
                  type="text"
                  name="ifsc_code"
                  value={affiliate.ifsc_code || ""}
                  onChange={handleChange}
                />
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={12}>
              <Form.Group>
                <Form.Label>Address</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  name="address"
                  value={affiliate.address || ""}
                  onChange={handleChange}
                />
              </Form.Group>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* HIERARCHY */}
      <Card className="mb-4">
        <Card.Body>
          <h5 className="mb-3">Hierarchy</h5>

          <Row className="mb-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label>Parent ID</Form.Label>
                <Form.Control
                  type="text"
                  value={affiliate.parent_id || "N/A"}
                  readOnly
                />
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group>
                <Form.Label>Created At</Form.Label>
                <Form.Control
                  type="text"
                  value={
                    affiliate.created_at
                      ? new Date(affiliate.created_at).toLocaleString()
                      : ""
                  }
                  readOnly
                />
              </Form.Group>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <div className="d-flex gap-2">
        <Button
          variant="primary"
          type="submit"
          onClick={handleSubmit}
          disabled={!edited || saving}
        >
          {saving ? (
            <>
              <Spinner
                as="span"
                animation="border"
                size="sm"
                role="status"
                aria-hidden="true"
                className="me-2"
              />
              Saving...
            </>
          ) : (
            "Save Changes"
          )}
        </Button>

        {edited && (
          <Button
            variant="secondary"
            onClick={() => {
              fetchAffiliate();
              setEdited(false);
              setError("");
            }}
          >
            Cancel
          </Button>
        )}
      </div>
    </Container>
  );
};

export default AffiliateDetail;
